"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { useTheme } from "./theme-provider";
import { AuthBox, InstallButton } from "./SidebarWidgets";
import { BirthOnboarding } from "./BirthOnboarding";
import { BrandMark } from "./BrandMark";
import { TwinkleStars } from "./TwinkleStars";
import { warm } from "../lib/fetchCache";

type NavItem = { href: string; label: string; icon: string; match?: (p: string) => boolean };

const PRIMARY: NavItem[] = [
  { href: "/", label: "운세", icon: "spark", match: (p) => p === "/" || p.startsWith("/fortune") || p.startsWith("/calendar") || p.startsWith("/storage") },
  { href: "/lifelog", label: "라이프로그", icon: "log" },
  { href: "/my", label: "내정보", icon: "me" },
  { href: "/ranking", label: "순위", icon: "trophy" },
  { href: "/community", label: "커뮤니티", icon: "chat" },
];

const SECONDARY: NavItem[] = [
  // 일단 숨김 — 추후 복구
  // { href: "/calendar", label: "달력 · 일진", icon: "cal" },
  // { href: "/storage", label: "보관함", icon: "book" },
  // { href: "/my", label: "마이페이지", icon: "user" },
];

// 일단 마이페이지(MY) 숨김 — 추후 복구: { href: "/my", label: "MY", icon: "user" }
const BOTTOM: NavItem[] = [...PRIMARY];

function Icon({ name, active }: { name: string; active?: boolean }) {
  const common = { width: 21, height: 21, viewBox: "0 0 24 24", style: { flex: "none" as const } };
  switch (name) {
    case "spark":
      return <svg {...common} fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round"><path d="M12 3l2 7 7 2-7 2-2 7-2-7-7-2 7-2z" /></svg>;
    case "trophy":
      return <svg {...common} fill="none" stroke="currentColor" strokeWidth={active ? 1.8 : 1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M7 4h10v5a5 5 0 0 1-10 0z" /><path d="M7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 1-3 3" /><path d="M10 14h4M9 20h6M12 14v6" /></svg>;
    case "chat":
      return <svg {...common} fill="none" stroke="currentColor" strokeWidth={active ? 1.8 : 1.5} strokeLinejoin="round"><path d="M4 5h16v11H8l-4 3z" /><path d="M8 9.5h8M8 12.5h5" strokeLinecap="round" /></svg>;
    case "cal":
      return <svg {...common} fill="none" stroke="currentColor" strokeWidth={active ? 1.8 : 1.5}><rect x="4" y="5" width="16" height="16" rx="2.5" /><line x1="4" y1="9.5" x2="20" y2="9.5" /><line x1="9" y1="3" x2="9" y2="6.5" /><line x1="15" y1="3" x2="15" y2="6.5" /></svg>;
    case "book":
      return <svg {...common} fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round"><path d="M6 4h12v17l-6-4.2L6 21z" /></svg>;
    case "user":
      return <svg {...common} fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth={1.5}><circle cx="12" cy="8" r="3.5" /><path d="M5 20c0-4.5 3-6.5 7-6.5s7 2 7 6.5" /></svg>;
    case "me":
      return <svg {...common} fill="none" stroke="currentColor" strokeWidth={active ? 1.9 : 1.5} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="10" r="2.6" fill={active ? "currentColor" : "none"} /><path d="M7.2 17.7c.8-2 2.6-3.1 4.8-3.1s4 1.1 4.8 3.1" /></svg>;
    case "menu":
      return <svg {...common} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"><line x1="4" y1="7" x2="20" y2="7" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="17" x2="20" y2="17" /></svg>;
    case "moon":
      return <svg {...common} fill="none" stroke="currentColor" strokeWidth={1.6}><path d="M21 12.8A8 8 0 1111.2 3 6.2 6.2 0 0021 12.8z" /></svg>;
    case "log":
      return <svg {...common} fill="none" stroke="currentColor" strokeWidth={active ? 1.8 : 1.5} strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="1.6" fill={active ? "currentColor" : "none"} /><circle cx="6" cy="12" r="1.6" fill={active ? "currentColor" : "none"} /><circle cx="6" cy="18" r="1.6" /><line x1="6" y1="7.6" x2="6" y2="10.4" /><line x1="6" y1="13.6" x2="6" y2="16.4" /><line x1="11" y1="6" x2="19" y2="6" /><line x1="11" y1="12" x2="19" y2="12" /><line x1="11" y1="18" x2="16" y2="18" /></svg>;
    default:
      return null;
  }
}

function isActive(item: NavItem, pathname: string) {
  if (item.match) return item.match(pathname);
  return item.href === "/" ? pathname === "/" : pathname.startsWith(item.href);
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { theme, toggle } = useTheme();
  const [standalone, setStandalone] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // 낙관적 네비게이션 — 탭을 누르는 즉시 활성 표시를 그 탭으로 옮긴다(스피너는 콘텐츠 영역 loading.tsx 담당).
  // 실제 라우트가 준비되어 pathname 이 갱신되면 해제.
  const [navTarget, setNavTarget] = useState<string | null>(null);

  // PWA(standalone) 여부 감지 — standalone 이면 하단 탭, 아니면 햄버거+드로어
  useEffect(() => {
    const modes = ["standalone", "fullscreen", "minimal-ui"].map((m) => window.matchMedia(`(display-mode: ${m})`));
    const compute = () =>
      modes.some((m) => m.matches) || (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    setStandalone(compute());
    const handler = () => setStandalone(compute());
    modes.forEach((m) => m.addEventListener?.("change", handler));
    return () => modes.forEach((m) => m.removeEventListener?.("change", handler));
  }, []);

  // 페이지 이동 완료(pathname 갱신) 시 드로어 닫기 + 낙관적 타깃 해제
  useEffect(() => {
    setDrawerOpen(false);
    setNavTarget(null);
  }, [pathname]);

  // 앱 로드 시 자주 쓰는 데이터를 미리 데워둠 — 탭 진입 전에 캐시를 채워 첫 진입도 빠르게.
  useEffect(() => {
    warm("/api/profile");
    warm("/api/community/boards");
  }, []);

  // 활성 판정 기준 경로 — 낙관적 타깃이 있으면 그것으로(하단 메뉴가 즉시 전환된 것처럼 보임)
  const navPath = navTarget ?? pathname;

  // 랜딩/로그인/관리자 화면은 사이드바·탭바 없이 단독 전체화면으로 렌더
  const bare = pathname === "/landing" || pathname.startsWith("/login") || pathname.startsWith("/admin");
  if (bare) {
    return <div style={{ minHeight: "100dvh", background: "var(--bg)" }}>{children}</div>;
  }

  const railLink = (item: NavItem) => {
    const active = isActive(item, navPath);
    return (
      <Link key={item.href} href={item.href} prefetch onClick={() => setNavTarget(item.href)} className="railitem navlink"
        style={{ display: "flex", alignItems: "center", gap: 13, padding: "12px 12px", borderRadius: 13,
          background: active ? "var(--accent-soft)" : "transparent",
          border: active ? "1px solid var(--line-strong)" : "1px solid transparent",
          color: active ? "var(--accent-bright)" : "var(--ink-dim)" }}>
        <Icon name={item.icon} active={active} />
        <span className="raillabel" style={{ fontSize: 14, fontWeight: active ? 600 : 400 }}>{item.label}</span>
      </Link>
    );
  };

  const drawerLink = (item: NavItem) => {
    const active = isActive(item, navPath);
    return (
      <Link key={item.href} href={item.href} prefetch onClick={() => setNavTarget(item.href)}
        style={{ display: "flex", alignItems: "center", gap: 13, padding: "13px 12px", borderRadius: 13,
          background: active ? "var(--accent-soft)" : "transparent",
          border: active ? "1px solid var(--line-strong)" : "1px solid transparent",
          color: active ? "var(--accent-bright)" : "var(--ink-dim)" }}>
        <Icon name={item.icon} active={active} />
        <span style={{ fontSize: 15, fontWeight: active ? 600 : 400 }}>{item.label}</span>
      </Link>
    );
  };

  // inRail=true 면 라벨에 raillabel 클래스를 붙여 좁은 레일(아이콘만)에서 텍스트를 숨긴다.
  // 드로어(inRail=false)에서는 항상 텍스트를 노출.
  const themeBtn = (inRail: boolean) => (
    <button onClick={toggle} className="navlink"
      style={{ display: "flex", alignItems: "center", gap: 13, padding: "12px 12px", borderRadius: 13,
        color: "var(--ink-dim)", background: "transparent", border: "1px solid transparent", cursor: "pointer", font: "inherit", width: "100%" }}>
      <Icon name="moon" />
      <span className={inRail ? "raillabel" : undefined} style={{ fontSize: 14 }}>{theme === "dark" ? "라이트 모드" : "다크 모드"}</span>
    </button>
  );

  return (
    <div className="web" data-standalone={standalone ? "true" : "false"}>
      <TwinkleStars />
      <div className="shell">
        {/* ===== RAIL (desktop sidebar) ===== */}
        <div className="rail">
          <Link href="/" className="railitem" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 26, padding: "0 6px" }}>
            <BrandMark size={30} />
            <span className="raillabel" style={{ fontWeight: 500, fontSize: 17, letterSpacing: "0.08em" }}>ALIGN</span>
          </Link>

          <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
            {PRIMARY.map(railLink)}
            {SECONDARY.length > 0 && <div className="raillabel" style={{ height: 1, background: "var(--line)", margin: "10px 8px" }} />}
            {SECONDARY.map(railLink)}
            <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 4, paddingTop: 8, borderTop: "1px solid var(--line)" }}>
              <InstallButton inRail />
              {themeBtn(true)}
              <AuthBox inRail />
            </div>
          </div>

          {/* 프리미엄 — 일단 숨김, 추후 복구
          <div className="railcred" style={{ marginTop: 4, padding: 15, borderRadius: 16, border: "1px solid var(--line-strong)", background: "linear-gradient(135deg,var(--accent-soft),var(--card-2))" }}>
            <div style={{ fontFamily: "'Noto Serif KR',serif", fontSize: 14, fontWeight: 600, color: "var(--gold-bright)" }}>프리미엄</div>
            <div style={{ fontSize: 11.5, color: "var(--ink-dim)", marginTop: 5, lineHeight: 1.5 }}>13체계 무제한 · 광고 없이</div>
            <div style={{ marginTop: 11, textAlign: "center", padding: "9px 0", borderRadius: 10, background: "var(--btn-grad)", fontSize: 12.5, fontWeight: 600, color: "var(--btn-ink)", cursor: "pointer" }}>업그레이드</div>
          </div>
          */}
        </div>

        {/* ===== MAIN ===== */}
        <div className="main">
          {/* mobile top bar — iOS PWA(black-translucent)에서 상태바(시간·배터리) 아래로
              콘텐츠가 파고들므로 safe-area-inset-top 만큼 위 여백을 확보해 겹침 방지 */}
          <div className="mtop" style={{ alignItems: "center", justifyContent: "space-between", padding: "calc(env(safe-area-inset-top) + 12px) 14px 12px", borderBottom: "1px solid var(--line)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* 햄버거 — 일반 웹 모드에서만 노출(CSS 제어) */}
              <button className="dm-hamburger" aria-label="메뉴 열기" onClick={() => setDrawerOpen(true)}>
                <Icon name="menu" />
              </button>
              <Link href="/" style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <BrandMark size={26} />
                <span style={{ fontWeight: 500, fontSize: 15, letterSpacing: "0.08em" }}>ALIGN</span>
              </Link>
            </div>
            {/* 우측 여백 — 설정은 하단 "내정보" 탭(/my)에서 진입 */}
          </div>

          <div className="fw-scroll" style={{ flex: 1, overflow: "auto", position: "relative" }}>
            {navTarget !== null && navTarget !== pathname ? (
              // 메뉴 전환 중 — 콘텐츠 영역에 즉시 스피너(라우트가 준비되면 새 페이지로 교체)
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "55vh", width: "100%" }}>
                <span role="progressbar" aria-label="불러오는 중"
                  style={{ width: 30, height: 30, borderRadius: "50%", border: "2.5px solid var(--line)", borderTopColor: "var(--gold-bright)", animation: "om-spin .7s linear infinite" }} />
              </div>
            ) : (
              children
            )}
          </div>

          {/* mobile bottom tab — standalone(PWA) 모드에서만 보임 */}
          <div className="bottomtab" style={{ flex: "none", justifyContent: "space-around", padding: "10px 12px calc(env(safe-area-inset-bottom) + 14px)", background: "var(--tabbar)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderTop: "1px solid var(--line)" }}>
            {BOTTOM.map((item) => {
              // navPath 기준 — 탭을 누르는 즉시 활성 표시가 그 탭으로 이동(스피너는 콘텐츠 loading.tsx)
              const active = isActive(item, navPath);
              return (
                <Link key={item.href + item.label} href={item.href} prefetch onClick={() => setNavTarget(item.href)} style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: 5, flex: 1, color: active ? "var(--accent-bright)" : "var(--ink-faint)", fontSize: 10, fontWeight: active ? 600 : 500 }}>
                  {active && (
                    <span style={{ position: "absolute", top: -8, width: 3, height: 3, borderRadius: "50%", background: "var(--accent-bright)" }} />
                  )}
                  <Icon name={item.icon} active={active} />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== 모바일 드로어 (일반 웹 모드) ===== */}
      <div className={"drawer-backdrop" + (drawerOpen ? " open" : "")} onClick={() => setDrawerOpen(false)} />
      <aside className={"drawer" + (drawerOpen ? " open" : "")} aria-hidden={!drawerOpen}>
        <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 22, padding: "0 6px" }}>
          <BrandMark size={28} />
          <span style={{ fontWeight: 500, fontSize: 16, letterSpacing: "0.08em" }}>ALIGN</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {PRIMARY.map(drawerLink)}
          {SECONDARY.length > 0 && <div style={{ height: 1, background: "var(--line)", margin: "10px 8px" }} />}
          {SECONDARY.map(drawerLink)}
        </div>
        <div style={{ marginTop: "auto", paddingTop: 12, display: "flex", flexDirection: "column", gap: 4, borderTop: "1px solid var(--line)" }}>
          <InstallButton inRail={false} />
          {themeBtn(false)}
          <AuthBox inRail={false} />
        </div>
      </aside>

      {/* 출생정보 없는 로그인 사용자(구글 가입자 등) 온보딩 */}
      <BirthOnboarding />
    </div>
  );
}
